<template>
  <div
    class="main-search critical-component-clear"
    :class="{
      'section-styles--bottom-padding': section.bottomPadding,
      'section-styles--divider': section.sectionDividers,
      'section-styles--top-padding': section.topPadding,
    }"
  >
    <div
      class="main-search__header container text-center"
      :class="{ 'main-search__header--no-results': showNoResults }"
    >
      <h1
        id="template-title"
        class="main-search__subtitle text-label-s"
        v-text="$string('search.page_subtitle')"
      />

      <h2
        class="main-search__title text-heading-s text-heading-m-desktop"
        v-html="searchTitle"
      />

      <p
        v-if="showNoResults"
        class="main-search__text text-body-xs text-body-s-desktop"
        v-text="searchText"
      />

      <btn
        v-if="showNoResults"
        class="main-search__back"
        :label="$string('search.back_to_homepage')"
        :show-icon="false"
        :url="$variable('routes.normalisedRoot')"
      />
    </div>

    <nosto-grid
      v-if="liquid.query"
      class="main-search__nosto"
      :class="{ 'main-search__nosto--no-results': showNoResults }"
      :liquid="liquid"
      :sort-by-options="sortByOptions"
      :storage-key="storageKey"
      :facet-exclusions="facetExclusions"
      @nosto-updated="nostoUpdated"
    />
  </div>
</template>

<script>

/**
 * Vue: Main search (main-search)
 * -----------------------------------------------------------------------------
 * Main search page component.
 *
 * @param {Object} liquid - Liquid data.
 * @param {Object} section - Section settings.
 * @param {Object} sortByOptions - Sort by options.
 *
 */
import NostoGrid from '~async/nosto-grid/nosto-grid'
import Btn from '~global/btn/btn'

import { waitFor } from '~/helpers/general'

export default {
  name: 'MainSearch',

  components: {
    NostoGrid,
    Btn,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        query: '',
      }),
    },
    section: {
      type: Object,
      default: () => ({
        bottomPadding: true,
        sectionDividers: true,
        topPadding: false,
      }),
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    facetExclusions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      nostoReady: false,
      query: '',
      storageKey: 'search-temp',
      totalResults: 0,
    }
  },

  computed: {

    /**
     * Compute search text.
     * @returns {String}
     */
    searchText() {
      if (!this.liquid.query) {
        return this.$string('search.no_query_page_text')
      }

      return this.$string('search.no_results_page_text')
    },

    /**
     * Compute search title.
     * @returns {String}
     */
    searchTitle() {
      if (!this.liquid.query) {
        return this.$string('search.no_query_page_title')
      }

      const query = this.nostoReady
        ? this.query
        : this.liquid.query

      if (this.showNoResults) {
        return this.$string('search.no_results_page_title', { replace: { query } })
      }

      const count = this.nostoReady
        ? this.totalResults
        : '<span></span>'

      return this.$string('search.page_title', {
        pluralise: this.totalResults,
        replace: { count, query },
      })
    },

    /**
     * Compute whether to show no results text.
     * - nosto has finished and returns no total results.
     * - There is no query.
     * @returns {Boolean}
     */
    showNoResults() {
      return !this.liquid.query || (this.nostoReady && this.totalResults < 1)
    },
  },

  methods: {

    /**
     * Handle nosto updated to get result count and query string.
     */
    nostoUpdated(data) {
      this.query = decodeURI(data.query)
      this.totalResults = data.totalResults
      this.nostoReady = true

      waitFor('nostojs', () => {
        nostojs(api => {
          api.recordSearchSubmit(this.query)
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import './main-search';
@import '~async/nosto-grid/nosto-grid';
@import '~async/product-card/product-card';
@import '~async/nosto-grid/nosto-filter';
@import '~async/nosto-grid/nosto-pagination';
@import '~async/in-grid-content-banner/in-grid-content-banner';
@import '~async/nosto-drawer/nosto-drawer';
@import '~async/nosto-horizontal-filter/nosto-horizontal-filter';
@import '~async/nosto-drawer/nosto-facet';
@import '~async/nosto-grid/nosto-active-values';
@import '~async/nosto-grid/nosto-active-value-tag';
</style>
